/** Typed functions for every route in knowledge/contracts/api.md. */
import { request, type Query } from "./transport";
import type {
  Account,
  AccountTestResult,
  Admin,
  Alert,
  ChannelNode,
  ChannelQuery,
  DeadLetter,
  EffectiveSettings,
  GlobalSettings,
  Guild,
  GuildSettingsResponse,
  Health,
  Job,
  JobCreate,
  ListResponse,
  LoginRequest,
  LoginResponse,
  Mapping,
  MappingBulkRequest,
  MappingCreate,
  MappingPatch,
  MappingQuery,
  PasswordChangeRequest,
  RevokedResponse,
  SettingsPatch,
  Snowflake,
  TargetChannel,
  TotpResetRequest,
  TotpResetResponse,
} from "./types";

const enc = encodeURIComponent;

export const authApi = {
  login: (body: LoginRequest) => request<LoginResponse>("POST", "/auth/login", { body }),
  logout: () => request<void>("POST", "/auth/logout"),
  me: () => request<Admin>("GET", "/auth/me"),
  enableTotp: (code: string) =>
    request<{ totp_enabled: true }>("POST", "/auth/totp/enable", { body: { code } }),
  /** C5: revokes every other session of the admin. */
  changePassword: (body: PasswordChangeRequest) =>
    request<RevokedResponse>("POST", "/auth/password", { body }),
  /** C5: starts replacing the authenticator; confirm with enableTotp(code). */
  resetTotp: (body: TotpResetRequest) =>
    request<TotpResetResponse>("POST", "/auth/totp/reset", { body }),
  revokeAllSessions: (includeCurrent = false) =>
    request<RevokedResponse>("POST", "/auth/sessions/revoke-all", {
      query: { include_current: includeCurrent ? true : undefined },
    }),
};

export const healthApi = {
  get: () => request<Health>("GET", "/health"),
};

export const accountsApi = {
  list: () => request<ListResponse<Account>>("GET", "/accounts"),
  create: (label: string, token: string) =>
    request<Account>("POST", "/accounts", { body: { label, token } }),
  replaceToken: (id: number, token: string) =>
    request<Account>("PUT", `/accounts/${id}/token`, { body: { token } }),
  test: (id: number) => request<AccountTestResult>("POST", `/accounts/${id}/test`),
};

export const guildsApi = {
  list: (q?: string) => request<ListResponse<Guild>>("GET", "/guilds", { query: { q } }),
  channels: (guildId: Snowflake, query: ChannelQuery = {}) =>
    request<ListResponse<ChannelNode>>("GET", `/guilds/${enc(guildId)}/channels`, {
      query: query as Query,
    }),
  patchSettings: (guildId: Snowflake, patch: SettingsPatch) =>
    request<GuildSettingsResponse>("PATCH", `/guilds/${enc(guildId)}/settings`, {
      body: patch,
    }),
};

export const targetsApi = {
  list: () => request<ListResponse<TargetChannel>>("GET", "/targets"),
};

export const mappingsApi = {
  list: (query: MappingQuery = {}) =>
    request<ListResponse<Mapping>>("GET", "/mappings", { query: query as Query }),
  get: (id: number) => request<Mapping>("GET", `/mappings/${id}`),
  create: (body: MappingCreate) => request<Mapping>("POST", "/mappings", { body }),
  patch: (id: number, body: MappingPatch) =>
    request<Mapping>("PATCH", `/mappings/${id}`, { body }),
  remove: (id: number) => request<void>("DELETE", `/mappings/${id}`),
  bulk: (body: MappingBulkRequest) =>
    request<{ updated: number }>("POST", "/mappings/bulk", { body }),
  effective: (id: number) => request<EffectiveSettings>("GET", `/mappings/${id}/effective`),
  test: (id: number) => request<{ job_id: string }>("POST", `/mappings/${id}/test`),
};

export const settingsApi = {
  global: () => request<GlobalSettings>("GET", "/settings/global"),
  patchGlobal: (patch: SettingsPatch) =>
    request<Omit<GlobalSettings, "spec">>("PATCH", "/settings/global", { body: patch }),
};

export const jobsApi = {
  list: () => request<ListResponse<Job>>("GET", "/jobs"),
  get: (id: string) => request<Job>("GET", `/jobs/${enc(id)}`),
  create: (body: JobCreate) => request<Job>("POST", "/jobs", { body }),
};

export const alertsApi = {
  list: (open?: boolean) =>
    request<ListResponse<Alert>>("GET", "/alerts", { query: { open } }),
};

export const deadLettersApi = {
  list: (limit = 100) =>
    request<ListResponse<DeadLetter>>("GET", "/dead-letters", { query: { limit } }),
  retry: (streamId: string) =>
    request<{ stream_id?: string; event_stream_id?: string } | undefined>(
      "POST",
      `/dead-letters/${enc(streamId)}/retry`,
    ),
};
